import { useState } from 'react';
import styled from 'styled-components';
import { apiGrantAllCredits } from '../../../api/userApi';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

const Modal = styled.div`
  background-color: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const ModalTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  color: gold;
  margin: 0;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  font-size: 20px;
  cursor: pointer;
  line-height: 1;
  &:hover { color: #fff; }
`;

const ModalBody = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.07em;
  display: block;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  color: #fff;
  font-size: 15px;
  padding: 9px 12px;
  outline: none;
  &:focus { border-color: gold; }
  &::-webkit-inner-spin-button, &::-webkit-outer-spin-button { opacity: 1; }
`;

const Textarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  color: #fff;
  font-size: 14px;
  padding: 9px 12px;
  outline: none;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  &:focus { border-color: gold; }
`;

const WarningBox = styled.div`
  background-color: rgba(255, 165, 0, 0.08);
  border: 1px solid rgba(255, 165, 0, 0.25);
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 12px;
  color: rgba(255, 200, 80, 0.85);
  line-height: 1.5;
`;

const ModalFooter = styled.div`
  padding: 14px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const CancelBtn = styled.button`
  padding: 8px 18px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: transparent;
  color: rgba(255, 255, 255, 0.55);
  font-size: 13px;
  cursor: pointer;
  &:hover { color: #fff; border-color: rgba(255,255,255,0.3); }
`;

const ConfirmBtn = styled.button<{ $loading: boolean }>`
  padding: 8px 22px;
  border-radius: 6px;
  border: 1px solid ${({ $loading }) => $loading ? 'rgba(255,255,255,0.1)' : 'gold'};
  background-color: ${({ $loading }) => $loading ? 'transparent' : 'rgba(255,215,0,0.14)'};
  color: ${({ $loading }) => $loading ? 'rgba(255,255,255,0.3)' : 'gold'};
  font-size: 13px;
  font-weight: 700;
  cursor: ${({ $loading }) => $loading ? 'default' : 'pointer'};
  &:hover:not(:disabled) { background-color: rgba(255,215,0,0.24); }
`;

const FeedbackText = styled.div<{ $type: 'success' | 'error' }>`
  font-size: 13px;
  color: ${({ $type }) => $type === 'success' ? '#4caf6f' : '#e05555'};
  padding: 0 20px 14px;
`;

interface Props {
    onClose: () => void;
    onGranted: (amount: number) => void;
}

const GrantAllCreditsModal: React.FC<Props> = ({ onClose, onGranted }) => {
    const [amount, setAmount] = useState('');
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

    const amountNum = parseInt(amount, 10);
    const isValid = !isNaN(amountNum) && amountNum >= 1;

    const handleConfirm = async () => {
        if (!isValid || loading) return;
        setLoading(true);
        setFeedback(null);
        try {
            const res = await apiGrantAllCredits(amountNum, notes.trim() || undefined);
            setFeedback({ msg: res.message, type: 'success' });
            onGranted(amountNum);
        } catch (err: any) {
            setFeedback({ msg: err?.message ?? 'Errore durante l\'operazione.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Overlay onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <Modal>
                <ModalHeader>
                    <ModalTitle>Distribuisci crediti a tutti</ModalTitle>
                    <CloseBtn onClick={onClose}>×</CloseBtn>
                </ModalHeader>

                <ModalBody>
                    <WarningBox>
                        Operazione globale — i crediti verranno assegnati a <strong>tutti</strong> gli utenti registrati. L'operazione è atomica e non reversibile.
                    </WarningBox>

                    <div>
                        <Label>Crediti da assegnare *</Label>
                        <Input
                            type="number"
                            min={1}
                            placeholder="es. 100"
                            value={amount}
                            onChange={e => setAmount(e.target.value)}
                        />
                    </div>

                    <div>
                        <Label>Note (opzionale)</Label>
                        <Textarea
                            maxLength={500}
                            placeholder="es. Regalo evento WrestleMania"
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                        />
                    </div>
                </ModalBody>

                {feedback && <FeedbackText $type={feedback.type}>{feedback.msg}</FeedbackText>}

                <ModalFooter>
                    <CancelBtn onClick={onClose}>Annulla</CancelBtn>
                    <ConfirmBtn $loading={loading || !isValid} disabled={loading || !isValid} onClick={handleConfirm}>
                        {loading ? 'Invio...' : 'Conferma'}
                    </ConfirmBtn>
                </ModalFooter>
            </Modal>
        </Overlay>
    );
};

export default GrantAllCreditsModal;
